import { UUID } from "../../../utils/typeDefinitions";
import IBimVisualizerApiProvider from "../IBimVisualizerApiProvider";

import AppDataProvider from "./AppDataProvider";
import IGbContext from "../../../interfaces/IGbContext";
import ApiCoreDocumentProvider from "../../documentProvider/ApiCoreDocumentProvider";
import User from "../../../model/User";
import IDocumentProvider from "../../documentProvider/IDocumentProvider";
import { RoomNoteSaveResult } from "../../coreApi/RoomNoteSaveResult";
import ICoreApiProvider from "../../coreApi/ICoreApiProvider";
import ICoreRoom from "../../../interfaces/ICoreRoom";
import ICoreApartmentBundles from "../../../interfaces/ICoreApartmentBundles";
import { BundleDetachResult, DetachRoomElementsBody } from "../../coreApi/BundleDetachResult";
import ICoreTenantSettings from "../../../interfaces/ICoreTenantSettings";
import { GbApiUserRightEnum, GbApiUserRights } from "../../../enums/GbApiUserRightEnum";
import { readCachedRights, userRightsCacheKey, writeCachedRights } from "../../coreApi/userRightsCache";

class GbContextAppDataProvider extends AppDataProvider  {
    gbContext: IGbContext;
    coreApiProvider: ICoreApiProvider;

    constructor(apiProvider: IBimVisualizerApiProvider, coreApiProvider: ICoreApiProvider, gbContext: IGbContext, docProvider: IDocumentProvider) {
        super(apiProvider, docProvider);
        this.coreApiProvider = coreApiProvider;
        this.gbContext = gbContext;
    }

        // Load spatial structure model
    loadRootProduct = async () => {
        const result = await this.apiProvider.getApartmentModelByGbContext(this.gbContext);
        return result;
    }

    getCurrentUser = async (): Promise<User | null> => {
        return await this.apiProvider.getCurrentUser(this.gbContext);
    }

    // A failed check isn't cached, so a passing CoreAPI hiccup doesn't hide features for the whole TTL
    loadUserRights = async (): Promise<GbApiUserRights> => {
        const userId = this.coreApiProvider.getUserId();
        const cacheKey = userId ? userRightsCacheKey(userId, this.gbContext) : null;

        const declared = Object.values(GbApiUserRightEnum);
        const cached = cacheKey ? readCachedRights(cacheKey, declared) : null;
        if (cached) return cached;

        const rights = await this.coreApiProvider.checkUserRights(this.gbContext, declared);
        if (!rights) return {};

        if (cacheKey) writeCachedRights(cacheKey, rights);
        return rights;
    }

    loadRoomNotes = async (): Promise<ICoreRoom[]> => {
        return await this.coreApiProvider.getApartmentRooms(this.gbContext) ?? [];
    }

    updateRoomNote = async (roomId: number, notes: string | null): Promise<RoomNoteSaveResult> => {
        return await this.coreApiProvider.updateRoomComment(this.gbContext, roomId, notes);
    }

    canInspectElements = (): boolean => {
        return !!this.gbContext.apartmentId && this.coreApiProvider.isAvailable();
    }

    loadElementBundles = async (): Promise<ICoreApartmentBundles | null> => {
        return await this.coreApiProvider.getApartmentBundles(this.gbContext);
    }

    detachBundleElements = async (body: DetachRoomElementsBody): Promise<BundleDetachResult> => {
        return await this.coreApiProvider.detachRoomElements(this.gbContext, body);
    }

    loadTenantSettings = async (): Promise<ICoreTenantSettings | null> => {
        return await this.coreApiProvider.getTenantSettings(this.gbContext);
    }
}

export default GbContextAppDataProvider;